import { useCallback, useState } from 'react';

import { REVIEWS_CHANNELS, WEBSITE_CHANNELS, ADS_CHANNELS } from 'src/data/performance';
import { formatRoute } from 'src/lib/routing';
import { ROUTES, ROUTE_PARAMS } from 'src/routes';
import {
  usePerformanceActions,
  useNotificationActions,
  useCustomerCanCreatePerformanceFeatureItem,
  useCustomerHasAccessToPerformance,
} from 'src/store';
import {
  useAdAccountsSelectionPopup,
  useConnectWebsiteAccountsPopup,
  useConnectReviewAccountsPopup,
  useUpgradePopup,
} from './popup';
import { useIsApplicationModeInfinity } from './useIsApplicationModeInfinity';

export const useConnectToChannel = () => {
  const notifications = useNotificationActions();
  const performanceActions = usePerformanceActions();
  const isApplicationModeInfinity = useIsApplicationModeInfinity();
  const customerHasAccessToPerformance = useCustomerHasAccessToPerformance();
  const openAdAccountsSelectionPopup = useAdAccountsSelectionPopup();
  const connectWebsiteAccountsPopup = useConnectWebsiteAccountsPopup();
  const connectReviewsAccountsPopup = useConnectReviewAccountsPopup();
  const customerCanCreatePerformanceFeatureItem = useCustomerCanCreatePerformanceFeatureItem();
  const openUpgradePopup = useUpgradePopup();

  const [connecting, setConnecting] = useState();

  const selectAdsAccount = useCallback(
    async (channel) => {
      const result = await openAdAccountsSelectionPopup(channel);

      if (channel && result?.selectedAccountIds?.length) {
        const link = formatRoute(ROUTES.PERFORMANCE_ADS, {
          [ROUTE_PARAMS.channel]: channel,
        });
        window.open(link);
      }
    },
    [openAdAccountsSelectionPopup]
  );

  const selectReviewsAccount = useCallback(
    async (channel) => {
      const result = await connectReviewsAccountsPopup(channel);

      if (channel && result?.url) {
        const link = formatRoute(ROUTES.PERFORMANCE_REVIEWS, {
          [ROUTE_PARAMS.channel]: channel,
        });
        window.open(link);
      }
    },
    [connectReviewsAccountsPopup]
  );

  const selectWebsiteAccount = useCallback(
    async (channel) => {
      const result = await connectWebsiteAccountsPopup(channel);

      if (channel && result?.urls?.length) {
        const link = formatRoute(ROUTES.PERFORMANCE_WEBSITE, {
          [ROUTE_PARAMS.channel]: channel,
        });
        window.open(link);
      }
    },
    [connectWebsiteAccountsPopup]
  );

  const connectToChannel = useCallback(
    async (channel) => {
      if (
        ADS_CHANNELS.includes(channel) &&
        (!customerHasAccessToPerformance || isApplicationModeInfinity)
      ) {
        const link = formatRoute(ROUTES.PERFORMANCE_ADS, {
          [ROUTE_PARAMS.channel]: channel,
        });
        window.open(link, '_blank');
        return;
      }

      if (!customerCanCreatePerformanceFeatureItem(channel)) {
        openUpgradePopup(channel);
        return;
      }

      if (REVIEWS_CHANNELS.includes(channel)) {
        await selectReviewsAccount(channel);
        return;
      }

      if (WEBSITE_CHANNELS.includes(channel)) {
        await selectWebsiteAccount(channel);
        return;
      }

      if (performanceActions.isChannelConnected(channel)) {
        await selectAdsAccount(channel);
        return;
      }

      try {
        setConnecting(true);
        await performanceActions.connectToChannel(channel);
        await selectAdsAccount(channel);
      } catch (error) {
        notifications.displayError(error);
      } finally {
        setConnecting(false);
      }
    },
    [
      customerHasAccessToPerformance,
      customerCanCreatePerformanceFeatureItem,
      openUpgradePopup,
      isApplicationModeInfinity,
      selectReviewsAccount,
      selectWebsiteAccount,
      selectAdsAccount,
      performanceActions,
      notifications,
    ]
  );

  return {
    connectToChannel,
    connecting,
  };
};
